import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReference } from "@/lib/api";
import { ConceptBar, CommandPalette, useConceptData, useCandidate, useSimulated, useHotkeys, useSelection, useVirtual, todayItems, criteriaColumns, filterRows, actionsFor, GROUP_LABEL, GROUP_ORDER, STATUS_LABEL, READY_LABEL, rel } from "@/concepts/shared";

const D = "editorial";
const GLYPH = { supported: ["●", "text-[color:var(--ready)]"], not_met: ["✕", "text-[color:var(--accent-text)]"], unknown: ["○", "opacity-45"] };

const Masthead = ({ onSearch, active }) => (
  <header className="flex items-end justify-between border-b border-[color:var(--ink)] px-8 pb-3 pt-6">
    <div className="flex items-end gap-10">
      <Link to="/concepts" className="leading-none" data-testid="ed-logo">
        <span className="mono block text-[10px] tracking-[0.35em] opacity-60">AVENIR</span>
        <span className="serif text-[34px] leading-none">Align<span className="text-[color:var(--accent)]">.</span></span>
      </Link>
      <nav className="flex gap-6 text-[13px]">
        {[["01", "Today", "today"], ["02", "Studies", "workbench"], ["03", "Participants", null], ["04", "Communications", null]].map(([n, l, s]) => (
          s ? <Link key={l} to={`/concepts/${D}/${s}`} data-testid={`ed-nav-${l.toLowerCase()}`} className={`flex gap-1.5 ${active === s ? "underline decoration-[color:var(--accent)] decoration-2 underline-offset-[6px]" : "opacity-70 hover:opacity-100"}`}><span className="mono text-[10px] opacity-60">{n}</span>{l}</Link>
            : <span key={l} className="flex gap-1.5 opacity-35" title="Planned module"><span className="mono text-[10px]">{n}</span>{l}</span>
        ))}
      </nav>
    </div>
    <button onClick={onSearch} className="mono flex items-center gap-3 border-b border-[color:var(--ink)] pb-1 text-[11px] opacity-80 hover:opacity-100" data-testid="ed-search">Search everything <kbd className="rounded-sm border border-[color:var(--rule)] px-1">⌘K</kbd></button>
  </header>
);

export const EditorialToday = () => {
  const { dash, studies, studyId } = useConceptData();
  const [open, setOpen] = useState(false);
  useHotkeys({ "mod+k": () => setOpen(true) });
  const items = todayItems(dash, D);
  if (!dash) return <div className="c-ed min-h-screen p-10">Loading…</div>;
  const m = dash.metrics;
  const date = new Date(dash.today);
  return (
    <div className="c-ed min-h-screen pb-24" data-testid="ed-today">
      <Masthead onSearch={() => setOpen(true)} active="today" />
      <div className="grid grid-cols-12 gap-8 px-8 pt-10">
        <section className="col-span-12 xl:col-span-8 c-rise">
          <p className="mono text-[11px] uppercase tracking-[0.2em] opacity-60">The recruitment brief · {date.toLocaleDateString("en-GB", { weekday: "long" })}</p>
          <h1 className="serif mt-2 text-[64px] leading-[0.95] md:text-[88px]">{date.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}</h1>
          <p className="mt-5 max-w-3xl text-[19px] leading-snug">
            <b className="font-semibold">{m.followups_due} follow-ups</b> are due{m.overdue ? <>, <span className="text-[color:var(--accent-text)]">{m.overdue} overdue</span></> : ""}.{" "}
            <b className="font-semibold">{m.awaiting_review}</b> candidates await review across {m.recruiting_studies} recruiting studies, and <b className="font-semibold">{m.handoff}</b> are approaching handoff to EDGE.
          </p>
          <div className="mt-10 flex items-baseline justify-between border-b border-[color:var(--ink)] pb-2">
            <h2 className="serif text-[28px]">The agenda</h2>
            <span className="mono text-[11px] opacity-60">{items.length} items, in priority order</span>
          </div>
          <ol data-testid="ed-agenda">
            {items.slice(0, 18).map((x, i) => (
              <li key={x.id} className="border-b border-[color:var(--rule)]">
                <Link to={x.to} className="group grid grid-cols-[48px_1fr_auto] items-baseline gap-4 py-3.5 hover:bg-black/[0.03]" data-testid={`ed-agenda-${i}`}>
                  <span className="serif text-[26px] opacity-40 tnum">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className={`mono mr-2 text-[10px] uppercase tracking-[0.16em] ${x.urgency === 0 ? "text-[color:var(--accent-text)]" : "opacity-60"}`}>{x.kind}</span>
                    <span className="text-[15px]">{x.title} — <b className="font-semibold">{x.who}</b></span>
                    <span className="mono ml-2 text-[11px] opacity-55">{x.ref} · {x.study}</span>
                  </span>
                  <span className="flex items-baseline gap-4 text-[12px]"><span className="opacity-60">{x.when}</span><span className="underline decoration-[color:var(--accent)] underline-offset-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">{x.action} →</span></span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
        <aside className="col-span-12 space-y-10 xl:col-span-4 xl:border-l xl:border-[color:var(--rule)] xl:pl-8 c-rise" style={{ animationDelay: "80ms" }}>
          <div>
            <h2 className="serif border-b border-[color:var(--ink)] pb-2 text-[24px]">Studies in recruitment</h2>
            {dash.study_progress.map((s) => {
              const ready = s.pipeline.ready_for_first_visit;
              return (
                <Link key={s.id} to={`/concepts/${D}/workbench?study=${s.id}`} className="block border-b border-[color:var(--rule)] py-3 hover:bg-black/[0.03]" data-testid={`ed-study-${s.reference}`}>
                  <p className="mono text-[10.5px] opacity-60">{s.reference} · {s.status}</p>
                  <p className="text-[14px] font-medium">{s.name}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[color:var(--rule)]"><div className="h-[3px] -translate-y-px bg-[color:var(--accent)]" style={{ width: `${Math.min(100, (ready / s.target) * 100)}%` }} /></div>
                    <span className="mono text-[11px] tnum">{ready}/{s.target}</span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div>
            <h2 className="serif border-b border-[color:var(--ink)] pb-2 text-[24px]">In the margins</h2>
            {dash.recent_activity.slice(0, 7).map((a) => (
              <p key={a.id} className="border-b border-[color:var(--rule)] py-2 text-[12.5px] leading-snug"><span className="mono mr-2 text-[10px] opacity-50">{rel(a.at)}</span>{a.summary}</p>
            ))}
          </div>
        </aside>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} studies={studies || []} studyId={studyId} contentClass="c-ed rounded-none border-[color:var(--ink)]" />
      <ConceptBar dir={D} screen="today" />
    </div>
  );
};

export const sentence = (c, ref) => {
  if (!ref) return [];
  const n = Object.fromEntries([...ref.conditions.map((x) => [x.code, x.name]), ...ref.drug_classes.map((x) => [`class:${x.key}`, x.key.replace("_", "/")]), ...ref.medications.map((x) => [`med:${x.id}`, x.name.split(" (")[0]])]);
  const out = [["Active participants aged "], [`${c.age_min ?? "any"}–${c.age_max ?? "any"}`, 1]];
  if (c.required_conditions.length) out.push([" with "], [c.required_conditions.map((x) => n[x]).join(" and "), 1]);
  if (c.required_medications.length) out.push([", using "], [c.required_medications.map((x) => n[x]).join(" or "), 1]);
  if (c.smoking_statuses.length) out.push([", "], [`${c.smoking_statuses.join("/")} smokers`, 1]);
  if (c.min_pack_years) out.push([" of "], [`≥${c.min_pack_years} pack-years`, 1]);
  const ex = [...c.excluded_conditions.map((x) => n[x]), ...(c.conflict_window_days ? [`a ${c.conflict_areas.join("/")} study in ${c.conflict_window_days} days`] : [])];
  if (ex.length) out.push([" — excluding "], [ex.join(", "), 2]);
  out.push(["."]);
  return out;
};

const Dossier = ({ studyId, pid, over, run }) => {
  const { data: d } = useCandidate(studyId, pid);
  if (!d) return <div className="p-8 text-[13px] opacity-60">Loading dossier…</div>;
  const ev = d.evaluation, p = d.participant;
  const { primary, others } = actionsFor(d);
  const col = (o) => ev.checks.filter((c) => c.outcome === o);
  const status = over[pid] || d.relationship?.status;
  return (
    <div className="c-slide px-8 py-7" key={pid} data-testid="ed-dossier">
      <p className="mono text-[11px] opacity-60">{p.participant_ref} · {p.age ?? "?"} · {p.sex || "—"} · {p.area}</p>
      <h2 className="serif mt-1 text-[44px] leading-none">{p.first_name} {p.last_name}</h2>
      <p className="serif mt-3 text-[21px] italic leading-snug">
        {GROUP_LABEL[ev.group]} match, <span className={ev.readiness === "ready" ? "" : "text-[color:var(--accent-text)]"}>{READY_LABEL[ev.readiness].toLowerCase()}</span>{status ? `, currently ${STATUS_LABEL[status].toLowerCase()}` : ", not yet in this study"}.
      </p>
      {(ev.restrictions.length > 0 || ev.conflicts.length > 0) && (
        <div className="mt-4 border-l-2 border-[color:var(--accent)] pl-3 text-[12.5px]">
          {[...ev.restrictions.map((r) => r.label), ...ev.conflicts.map((c) => c.label)].map((t) => <p key={t}>{t}</p>)}
        </div>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
        {primary && (
          <button onClick={() => run(pid, primary)} className="flex items-center gap-3 bg-[color:var(--ink)] px-5 py-3 text-[14px] text-[color:var(--paper)] hover:bg-[color:var(--accent-text)]" data-testid="ed-primary-action">
            {primary.label} <kbd className="mono rounded-sm border border-white/30 px-1 text-[10px]">↵</kbd>
          </button>
        )}
        {others.map((a) => (
          <button key={a.key} disabled={!a.enabled} title={a.reason || ""} onClick={() => run(pid, a)} data-testid={`ed-action-${a.key}`}
            className="text-[12.5px] underline decoration-[color:var(--rule)] underline-offset-4 hover:decoration-[color:var(--ink)] disabled:cursor-not-allowed disabled:no-underline disabled:opacity-35">{a.label}</button>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-3 gap-5 border-t border-[color:var(--ink)] pt-4">
        {[["Supports", "supported"], ["Against", "not_met"], ["Unknown", "unknown"]].map(([t, o]) => (
          <div key={o}>
            <p className="mono mb-2 text-[10px] uppercase tracking-[0.18em]"><span className={GLYPH[o][1]}>{GLYPH[o][0]}</span> {t} · {col(o).length}</p>
            {col(o).map((c) => <p key={c.key + c.kind} className="mb-2 text-[12.5px] leading-snug"><b className="font-medium">{c.short || c.label}</b><br /><span className="opacity-60">{c.detail}</span></p>)}
          </div>
        ))}
      </div>
    </div>
  );
};

export const EditorialWorkbench = () => {
  const { wb, studies, studyId } = useConceptData();
  const { data: ref } = useReference();
  const [group, setGroup] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { over, run } = useSimulated();
  const rows = useMemo(() => filterRows(wb?.rows || [], { group, q }), [wb, group, q]);
  const sel = useSelection(rows);
  const box = useRef(null);
  const search = useRef(null);
  const v = useVirtual(rows.length, 46, box);
  const step = (d) => { sel.move(d); v.reveal(Math.min(rows.length - 1, Math.max(0, sel.idx + d))); };
  const { data: cand } = useCandidate(studyId, sel.pid);
  useHotkeys({ "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1), "/": () => search.current?.focus(), enter: () => run(sel.pid, actionsFor(cand).primary) });
  if (!wb) return <div className="c-ed min-h-screen p-10">Loading…</div>;
  const cols = criteriaColumns(wb.rows);
  const s = wb.study;
  const counts = Object.fromEntries(GROUP_ORDER.map((g) => [g, wb.rows.filter((r) => r.group === g).length]));
  return (
    <div className="c-ed flex h-screen flex-col" data-testid="ed-workbench">
      <Masthead onSearch={() => setOpen(true)} active="workbench" />
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_460px]">
        <div className="flex min-h-0 flex-col">
          <div className="px-8 pb-4 pt-6">
            <p className="mono text-[11px] uppercase tracking-[0.2em] opacity-60">{s.reference} · {s.therapeutic_area} · {s.phase} · target {s.target}</p>
            <h1 className="serif mt-1 text-[44px] leading-[1]">{s.name}</h1>
            <p className="mt-3 max-w-4xl text-[15px] leading-relaxed" data-testid="ed-criteria-sentence">
              {sentence(s.criteria, ref).map(([t, k], i) => <span key={i} className={k === 1 ? "border-b-2 border-[color:var(--ink)] font-medium" : k === 2 ? "border-b-2 border-[color:var(--accent)] font-medium" : ""}>{t}</span>)}
            </p>
            <div className="mt-5 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b border-[color:var(--ink)] pb-2">
              {[["all", "All", wb.rows.length], ...GROUP_ORDER.map((g) => [g, GROUP_LABEL[g], counts[g]])].map(([k, l, n]) => (
                <button key={k} onClick={() => setGroup(k)} data-testid={`ed-group-${k}`} className={`flex items-baseline gap-1.5 ${group === k ? "" : "opacity-45 hover:opacity-80"}`}>
                  <span className="serif text-[30px] leading-none tnum">{n}</span><span className="text-[12px]">{l}</span>
                </button>
              ))}
              <input ref={search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or ID  ( / )" className="ml-auto w-56 border-b border-[color:var(--rule)] bg-transparent py-1 text-[12.5px] outline-none placeholder:opacity-50 focus:border-[color:var(--ink)]" data-testid="ed-filter-input" />
            </div>
          </div>
          <div className="grid items-end px-8 text-[10px]" style={{ gridTemplateColumns: `44px 210px 92px repeat(${cols.length}, 30px) 1fr` }}>
            <span className="mono pb-2 opacity-50">№</span><span className="mono pb-2 opacity-50">PARTICIPANT</span><span className="mono pb-2 opacity-50">GROUP</span>
            {cols.map((c) => <span key={c} className="mono h-[96px] pb-2 opacity-60 [writing-mode:vertical-rl] rotate-180 truncate" title={c}>{c}</span>)}
            <span className="mono pb-2 opacity-50">STATUS · NEXT</span>
          </div>
          <div ref={box} className="min-h-0 flex-1 overflow-auto border-t border-[color:var(--ink)] px-8" data-testid="ed-matrix">
            <div style={{ height: v.total, position: "relative" }}>
              {rows.slice(v.start, v.end).map((r, i) => {
                const on = r.participant_id === sel.pid;
                const st = over[r.participant_id] || r.status;
                return (
                  <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} data-testid={`ed-row-${r.participant_ref}`}
                    className={`absolute left-0 right-0 grid h-[46px] items-center border-b border-[color:var(--rule)] text-left text-[13px] ${on ? "bg-[color:var(--ink)] text-[color:var(--paper)]" : "hover:bg-black/[0.04]"}`}
                    style={{ top: v.top + i * 46, gridTemplateColumns: `44px 210px 92px repeat(${cols.length}, 30px) 1fr` }}>
                    <span className="mono text-[11px] opacity-50">{r.rank}</span>
                    <span className="truncate"><b className="font-medium">{r.name}</b> <span className="mono text-[10.5px] opacity-55">{r.participant_ref}</span></span>
                    <span className="text-[11.5px]">{GROUP_LABEL[r.group]}{r.readiness !== "ready" && <span className={on ? "" : "text-[color:var(--accent-text)]"}> ·{r.readiness === "blocked" ? "✕" : "!"}</span>}</span>
                    {cols.map((c, ci) => { const e = r.evidence[ci]; return <span key={c} className={`text-center ${on ? "" : e ? GLYPH[e.outcome][1] : ""}`}>{e ? GLYPH[e.outcome][0] : ""}</span>; })}
                    <span className="truncate text-[12px] opacity-80">{st ? STATUS_LABEL[st] : "—"} · {r.next_action}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <p className="mono border-t border-[color:var(--rule)] px-8 py-2 text-[10.5px] opacity-60">● supported by record  ✕ confirmed not met  ○ unknown — never treated as failure · ! restricted  ✕ blocked · J/K move · ↵ primary action · ⌘K search</p>
        </div>
        <aside className="min-h-0 overflow-auto border-l border-[color:var(--ink)] bg-white/40">
          {sel.pid && <Dossier studyId={studyId} pid={sel.pid} over={over} run={run} />}
        </aside>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} rows={wb.rows} studies={studies || []} studyId={studyId} contentClass="c-ed rounded-none border-[color:var(--ink)]" />
      <ConceptBar dir={D} screen="workbench" />
    </div>
  );
};
